import {
  Briefcase,
  Building2,
  Compass,
  FileText,
  HardHat,
  LineChart,
  MapPin,
  Eye,
  Leaf,
  Hotel,
  ShoppingBag,
  Palette,
  Megaphone,
} from "lucide-react";

export const SERVICES = [
  {
    slug: "project-management-consultancy",
    title: "Project Management Consultancy",
    icon: Briefcase,
    short:
      "End-to-end PMC services for public and private projects — scope, schedule, cost and quality under one accountable team.",
    hero: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?q=85&w=2400",
    scope: [
      "Project initiation, governance and stakeholder management",
      "Master schedule and integrated baseline planning",
      "Tendering, vendor selection and contract administration",
      "Cost control, change management and risk register",
      "QA/QC oversight and HSE compliance",
      "Monthly client dashboards and executive reporting",
    ],
    workflow: [
      "Mobilisation & governance setup",
      "Baseline plan, budget and risk register",
      "Tender and award support",
      "Construction supervision and reporting",
      "Commissioning and handover",
    ],
    benefits: [
      "Single point of accountability",
      "Predictable schedule and cost performance",
      "Independent quality assurance",
      "Disciplined contract administration",
    ],
    industries: ["Government", "Infrastructure", "Hospitality", "Retail", "Healthcare"],
    faqs: [
      { q: "Do you handle EPC and turnkey projects?", a: "Yes. We support both EPC and traditional design-bid-build delivery with appropriate governance models." },
      { q: "Can you operate from our office?", a: "Absolutely. We routinely embed PMC teams on-site for the project duration." },
    ],
  },
  {
    slug: "structural-design-consultancy",
    title: "Structural Design Consultancy",
    icon: Building2,
    short:
      "Resilient structural engineering for high-rises, infrastructure, hospitality and industrial buildings.",
    hero: "https://images.unsplash.com/photo-1464440579920-df688e377d3c?q=85&w=2400",
    scope: [
      "RCC, steel, composite and pre-engineered structures",
      "Seismic and wind engineering as per IS codes",
      "Foundation design including pile and raft systems",
      "Peer review and third-party proof checking",
      "Retrofit, strengthening and condition assessment",
    ],
    workflow: ["Concept", "Preliminary design", "Detailed design", "Tender drawings", "Site issued drawings"],
    benefits: ["Efficient member sizing", "Code-compliant safety", "Constructability focus", "Reduced material cost"],
    industries: ["Commercial Buildings", "Infrastructure", "Hospitality", "Industrial"],
    faqs: [
      { q: "Which design standards do you follow?", a: "IS, IRC, IBC, ASCE and Eurocodes — selected per project context and client preference." },
    ],
  },
  {
    slug: "building-planning-architecture",
    title: "Building Planning & Architecture",
    icon: Compass,
    short:
      "Architecture that balances function, climate, culture and commercial viability — from master-plan to interior.",
    hero: "https://images.unsplash.com/photo-1674280325498-accd8919133b?q=85&w=2400",
    scope: [
      "Site analysis and master-planning",
      "Architectural concept and schematic design",
      "Interior design and FF&E specifications",
      "Building services coordination (MEP)",
      "Statutory approvals and liaison",
    ],
    workflow: ["Brief", "Concept", "Schematics", "Detailed design", "Construction administration"],
    benefits: ["Distinctive identity", "Climate-responsive design", "Permit-ready documentation"],
    industries: ["Hospitality", "Retail", "Institutional", "Residential", "Cultural"],
    faqs: [
      { q: "Do you provide interior design as well?", a: "Yes — interiors are delivered as an integrated workstream or as a stand-alone commission." },
    ],
  },
  {
    slug: "detailed-project-report",
    title: "Detailed Project Report (DPR)",
    icon: FileText,
    short:
      "Bankable DPRs for government, PSU and private sponsors — engineered for approval and finance.",
    hero: "https://images.pexels.com/photos/11310491/pexels-photo-11310491.jpeg?w=2400",
    scope: [
      "Need assessment and demand analysis",
      "Technical feasibility and engineering studies",
      "Cost estimation, BOQ and rate analysis",
      "Financial appraisal, IRR and sensitivity",
      "Environmental and social impact assessment",
      "Implementation schedule and risk matrix",
    ],
    workflow: ["Data collection", "Surveys", "Engineering & costing", "Financial modelling", "Draft & final DPR"],
    benefits: ["Approval-ready documents", "Finance-ready models", "Stakeholder consensus"],
    industries: ["Government", "Smart Cities", "Tourism", "Infrastructure"],
    faqs: [
      { q: "Which agencies have you submitted DPRs to?", a: "Including state PWDs, urban development authorities, MoHUA schemes and PSU clients." },
    ],
  },
  {
    slug: "construction-consultancy",
    title: "Construction Consultancy",
    icon: HardHat,
    short:
      "Independent construction advice for sponsors, contractors and lenders — buildable, safe and on schedule.",
    hero: "https://images.unsplash.com/photo-1529926691761-20fb82067c71?q=85&w=2400",
    scope: [
      "Constructability reviews and value engineering",
      "Method statements and temporary works",
      "Procurement strategy and packaging",
      "Claims, disputes and contract advisory",
    ],
    workflow: ["Mobilise", "Diagnose", "Recommend", "Implement", "Monitor"],
    benefits: ["Reduced cost and rework", "Improved safety", "Faster delivery"],
    industries: ["Infrastructure", "Industrial", "Commercial"],
    faqs: [],
  },
  {
    slug: "monitoring-and-evaluation",
    title: "Monitoring & Evaluation",
    icon: LineChart,
    short:
      "Third-party M&E for sponsors, lenders and government — independent, data-led, dashboard-driven.",
    hero: "https://images.pexels.com/photos/8961133/pexels-photo-8961133.jpeg?w=2400",
    scope: [
      "Physical and financial progress tracking",
      "Quality audits and compliance reviews",
      "Beneficiary surveys and outcome studies",
      "Mid-term and end-line evaluations",
    ],
    workflow: ["Inception", "Field deployment", "Data analysis", "Reporting"],
    benefits: ["Independent assurance", "Actionable insight", "Lender comfort"],
    industries: ["Government", "Smart Cities", "Donor-funded"],
    faqs: [],
  },
  {
    slug: "survey-investigation",
    title: "Survey & Investigation",
    icon: MapPin,
    short:
      "Topographical, geotechnical, hydrological and utility surveys with modern instrumentation and drone mapping.",
    hero: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?q=85&w=2400",
    scope: [
      "Topographical and contour surveys",
      "Geotechnical investigation and lab testing",
      "Hydrological and hydraulic studies",
      "Traffic and origin-destination surveys",
      "Drone photogrammetry and LiDAR",
    ],
    workflow: ["Scoping", "Field mobilisation", "Data acquisition", "Processing", "Report"],
    benefits: ["High accuracy data", "Risk-reduced design", "Faster project starts"],
    industries: ["Infrastructure", "Urban Development", "Industrial"],
    faqs: [],
  },
  {
    slug: "site-supervision",
    title: "Site Supervision",
    icon: Eye,
    short:
      "Resident engineering, quality control and safety supervision teams deployed on your construction sites.",
    hero: "https://images.pexels.com/photos/8961134/pexels-photo-8961134.jpeg?w=2400",
    scope: [
      "Resident engineer deployment",
      "Material testing and quality records",
      "Safety officer and toolbox talks",
      "Daily, weekly and monthly progress reports",
    ],
    workflow: ["Mobilise", "Inspect", "Document", "Report", "Close-out"],
    benefits: ["Reduced defects", "Safer sites", "Auditable records"],
    industries: ["Infrastructure", "Hospitality", "Industrial"],
    faqs: [],
  },
  {
    slug: "environmental-consultancy",
    title: "Environmental Consultancy",
    icon: Leaf,
    short:
      "EIA, EMP, green building certification and climate-resilience advisory aligned with MoEFCC and IGBC.",
    hero: "https://images.unsplash.com/photo-1464440579920-df688e377d3c?q=85&w=2400",
    scope: [
      "EIA, EMP and CRZ clearance",
      "IGBC / GRIHA / LEED certification support",
      "Carbon footprint and ESG advisory",
      "Climate vulnerability and adaptation studies",
    ],
    workflow: ["Baseline", "Impact study", "Mitigation plan", "Approval", "Monitoring"],
    benefits: ["Approval readiness", "Lower lifecycle cost", "ESG credibility"],
    industries: ["Government", "Industrial", "Hospitality", "Real Estate"],
    faqs: [],
  },
  {
    slug: "hospitality-consultancy",
    title: "Hospitality Consultancy",
    icon: Hotel,
    short:
      "From feasibility to brand selection, F&B planning, OS&E and operator onboarding — for hotels and resorts.",
    hero: "https://images.unsplash.com/photo-1777245398213-e95bb34c442a?q=85&w=2400",
    scope: [
      "Market study and brand positioning",
      "Operator search and HMA negotiation",
      "Concept design and FF&E coordination",
      "Pre-opening, OS&E and SOP set-up",
      "Asset management and revenue advisory",
    ],
    workflow: ["Discovery", "Feasibility", "Brand & operator", "Design & build", "Pre-opening", "Operate"],
    benefits: ["Right-sized investment", "Higher RevPAR", "Better operator fit"],
    industries: ["Hotels", "Resorts", "Service Apartments", "Restaurants"],
    faqs: [],
  },
  {
    slug: "retail-consultancy",
    title: "Retail Consultancy",
    icon: ShoppingBag,
    short:
      "Mall planning, store design, expansion strategy and visual merchandising for modern retail.",
    hero: "https://images.pexels.com/photos/18206310/pexels-photo-18206310.jpeg?w=2400",
    scope: [
      "Catchment study and site selection",
      "Mall master-planning and tenant mix",
      "Store design and visual merchandising",
      "Roll-out program management",
    ],
    workflow: ["Strategy", "Site", "Design", "Build", "Open"],
    benefits: ["Optimised footfall", "Higher sales-per-sqft", "Faster roll-outs"],
    industries: ["Malls", "High-street", "F&B Chains", "Convenience"],
    faqs: [],
  },
  {
    slug: "art-cultural-consultancy",
    title: "Art & Cultural Consultancy",
    icon: Palette,
    short:
      "Museums, galleries, auditoriums and heritage conservation — design, curation and operations.",
    hero: "https://images.unsplash.com/photo-1683290844875-0eee4089069a?q=85&w=2400",
    scope: [
      "Museum and gallery design",
      "Curatorial and exhibition planning",
      "Heritage conservation and adaptive reuse",
      "Auditorium and convention design",
    ],
    workflow: ["Curate", "Design", "Build", "Activate"],
    benefits: ["Storytelling design", "Visitor engagement", "Heritage value"],
    industries: ["Government", "Cultural Trusts", "Tourism"],
    faqs: [],
  },
  {
    slug: "marketing-business-consultancy",
    title: "Marketing & Business Consultancy",
    icon: Megaphone,
    short:
      "Go-to-market, brand and growth strategy for hospitality, retail and infrastructure businesses.",
    hero: "https://images.unsplash.com/photo-1614521084980-811d04f6c6cb?q=85&w=2400",
    scope: [
      "Market entry and growth strategy",
      "Brand positioning and identity",
      "Digital marketing and lead funnels",
      "Channel and partnership development",
    ],
    workflow: ["Diagnose", "Strategise", "Activate", "Scale"],
    benefits: ["Sharper positioning", "Predictable pipeline", "Faster growth"],
    industries: ["Hospitality", "Retail", "Real Estate"],
    faqs: [],
  },
];
